import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { PageTitle } from "@/components/CrudPage";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Mail } from "lucide-react";

const CODECS = ["opus", "g722", "ulaw", "alaw", "g729"];

function Field({ label, children, help }) {
  return (
    <div>
      <Label className="text-xs text-muted-foreground">{label}</Label>
      <div className="mt-1.5">{children}</div>
      {help && <p className="text-[11px] text-muted-foreground mt-1">{help}</p>}
    </div>
  );
}

export default function Settings() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [mail, setMail] = useState(null);
  const [testTo, setTestTo] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => { api.get("/settings").then((r) => setS(r.data)); }, []);
  useEffect(() => { api.get("/email/status").then((r) => setMail(r.data)).catch(() => {}); }, []);
  const setF = (k, v) => setS((p) => ({ ...p, [k]: v }));
  const toggleCodec = (c) => setF("codecs", s.codecs.includes(c) ? s.codecs.filter((x) => x !== c) : [...s.codecs, c]);

  const sendTest = async () => {
    if (!testTo) return toast.error("Enter a recipient email");
    setSending(true);
    try {
      await api.post("/email/test", { to: testTo });
      toast.success(`Test email accepted for ${testTo}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSending(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...s };
      ["sip_port", "tls_port", "rtp_start", "rtp_end"].forEach((k) => (payload[k] = Number(payload[k])));
      const { data } = await api.put("/settings", payload);
      setS(data); toast.success("Settings saved");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  if (!s) return <div className="text-muted-foreground">Loading settings…</div>;

  return (
    <div>
      <PageTitle
        title="Settings"
        subtitle="Global PBX identity and SIP/RTP parameters. These feed provisioning details and the generated Asterisk config."
        actions={<Button onClick={save} disabled={saving} data-testid="settings-save"
          className="bg-[hsl(var(--gold))] text-[hsl(var(--primary-foreground))] hover:bg-[hsl(var(--gold-hover))] font-semibold">{saving ? "Saving…" : "Save Settings"}</Button>}
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="rounded-xl border border-border bg-card p-6 space-y-4">
          <h3 className="font-display font-semibold">Identity & Network</h3>
          <Field label="Company Name"><Input value={s.company_name} onChange={(e) => setF("company_name", e.target.value)} className="bg-secondary/40 border-border" data-testid="set-company" /></Field>
          <Field label="SIP Domain / Realm" help="Used as the SIP realm your devices register against."><Input value={s.sip_domain} onChange={(e) => setF("sip_domain", e.target.value)} className="bg-secondary/40 border-border font-mono" data-testid="set-domain" /></Field>
          <Field label="Server Host (FQDN)"><Input value={s.server_host} onChange={(e) => setF("server_host", e.target.value)} placeholder="pbx.mycompany.com" className="bg-secondary/40 border-border font-mono" /></Field>
          <Field label="External / Public IP" help="The Azure VM public IP for NAT & SIP signalling."><Input value={s.external_ip} onChange={(e) => setF("external_ip", e.target.value)} placeholder="20.50.x.x" className="bg-secondary/40 border-border font-mono" /></Field>
        </div>

        <div className="rounded-xl border border-border bg-card p-6 space-y-4">
          <h3 className="font-display font-semibold">SIP & RTP</h3>
          <div className="grid grid-cols-2 gap-3">
            <Field label="SIP Port"><Input type="number" value={s.sip_port} onChange={(e) => setF("sip_port", e.target.value)} className="bg-secondary/40 border-border" /></Field>
            <Field label="TLS Port"><Input type="number" value={s.tls_port} onChange={(e) => setF("tls_port", e.target.value)} className="bg-secondary/40 border-border" /></Field>
            <Field label="RTP Start"><Input type="number" value={s.rtp_start} onChange={(e) => setF("rtp_start", e.target.value)} className="bg-secondary/40 border-border" /></Field>
            <Field label="RTP End"><Input type="number" value={s.rtp_end} onChange={(e) => setF("rtp_end", e.target.value)} className="bg-secondary/40 border-border" /></Field>
          </div>
          <Field label="Default Transport">
            <Select value={s.transport} onValueChange={(v) => setF("transport", v)}>
              <SelectTrigger className="bg-secondary/40 border-border"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="tls">TLS (recommended)</SelectItem>
                <SelectItem value="udp">UDP</SelectItem>
                <SelectItem value="tcp">TCP</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="Codecs">
            <div className="flex flex-wrap gap-2">
              {CODECS.map((c) => {
                const active = s.codecs.includes(c);
                return <button key={c} onClick={() => toggleCodec(c)} type="button"
                  className={`px-2.5 py-1 rounded-md text-xs border transition-colors ${active ? "bg-[hsl(var(--gold)/0.15)] border-[hsl(var(--gold)/0.5)] text-[hsl(var(--gold))]" : "border-border text-muted-foreground"}`}>{c}</button>;
              })}
            </div>
          </Field>
          <div className="flex items-center gap-3 pt-1">
            <Switch checked={s.recording_enabled} onCheckedChange={(v) => setF("recording_enabled", v)} data-testid="set-recording" />
            <span className="text-sm">Call recording enabled</span>
          </div>
        </div>
      </div>

      {/* Voicemail → Email (Microsoft Graph) */}
      <div className="rounded-xl border border-border bg-card p-6 mt-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 className="font-display font-semibold flex items-center gap-2">
              <Mail size={17} className="text-[hsl(var(--gold))]" /> Voicemail → Email
            </h3>
            <p className="text-sm text-muted-foreground mt-1">
              Outbound mail via Microsoft Graph. Configure the Azure app registration on your VM
              (<span className="font-mono text-xs">MS_TENANT_ID · MS_CLIENT_ID · MS_CLIENT_SECRET</span>).
            </p>
          </div>
          <div className="flex items-center gap-2" data-testid="email-status">
            <span className="text-xs text-muted-foreground">Sender</span>
            <span className="font-mono text-sm">{mail?.sender || "…"}</span>
            {mail && (mail.configured
              ? <span className="px-2 py-0.5 rounded-md text-xs bg-emerald-500/12 text-emerald-400 border border-emerald-500/30">Connected</span>
              : <span className="px-2 py-0.5 rounded-md text-xs bg-[hsl(var(--gold)/0.14)] text-[hsl(var(--gold))] border border-[hsl(var(--gold)/0.3)]">Not configured</span>)}
          </div>
        </div>
        <div className="flex flex-wrap items-end gap-3 mt-4">
          <div className="flex-1 min-w-[220px]">
            <Label className="text-xs text-muted-foreground">Send a test email to</Label>
            <Input value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="you@company.com"
              className="bg-secondary/40 border-border mt-1.5" data-testid="email-test-to" />
          </div>
          <Button onClick={sendTest} disabled={sending} data-testid="email-test-send"
            className="bg-[hsl(var(--gold))] text-[hsl(var(--primary-foreground))] hover:bg-[hsl(var(--gold-hover))] font-semibold">
            {sending ? "Sending…" : "Send Test"}
          </Button>
        </div>
        {mail && !mail.configured && (
          <p className="text-[11px] text-muted-foreground mt-3">
            Set the three <span className="font-mono">MS_*</span> variables on the VM and grant the app the
            <span className="font-mono"> Mail.Send</span> application permission (admin consent). Enable “Voicemail → Email” per extension.
          </p>
        )}
      </div>
    </div>
  );
}
